// ============================================================================
// OPTIMIZED ATTENDANCE SERVICE - PERFORMANCE ENHANCED
// frontend/src/services/locationattendance.service.ts
// ============================================================================

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  query,
  where,
  onSnapshot,
  getDocs,
  Firestore,
  QuerySnapshot,
  DocumentData,
  Unsubscribe,
  limit
} from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyC_cwJ2lBqsDMzWmGIlcBqyBb2BqP3I6aA",
  authDomain: "face-authentication-app-44ddb.firebaseapp.com",
  projectId: "face-authentication-app-44ddb",
  storageBucket: "face-authentication-app-44ddb.firebasestorage.app",
  messagingSenderId: "541867932129",
  appId: "1:541867932129:android:ed73e3859b053c1555faa0"
};

export interface AttendanceRecord {
  employeeId: string;
  checkIn: string;
  checkOut?: string | null;
  date: string;
  location: string;
  locationId: string;
  locationLat: number;
  locationLng: number;
  isWithinGeofence: boolean;
  workStatus: string;
  totalHours: number;
}

export interface CurrentCheckIn {
  employeeId: string;
  location: string;
  locationId: string;
  locationLat: number;
  locationLng: number;
  checkInTime: string;
  workStatus: string;
  isActive: boolean;
  isWithinGeofence: boolean;
  totalHours: number;
  checkInLocation?: string;
  locationName?: string;
}

export type CurrentCheckInsCallback = (checkIns: CurrentCheckIn[]) => void;

// ============================================================================
// DATE CACHE
// ============================================================================

const DateCache = {
  _todayCache: null as { date: string; start: Date; end: Date; timestamp: number } | null,

  getTodayRange(): { start: Date; end: Date; dateString: string } {
    const now = Date.now();
    
    if (this._todayCache && now - this._todayCache.timestamp < 60000) {
      return {
        start: new Date(this._todayCache.start),
        end: new Date(this._todayCache.end),
        dateString: this._todayCache.date
      };
    }

    const today = new Date();
    const dubaiTime = new Date(today.toLocaleString("en-US", { timeZone: "Asia/Dubai" }));
    
    const startOfDay = new Date(dubaiTime);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(dubaiTime);
    endOfDay.setHours(23, 59, 59, 999);
    
    const year = dubaiTime.getFullYear();
    const month = String(dubaiTime.getMonth() + 1).padStart(2, '0');
    const day = String(dubaiTime.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    this._todayCache = {
      date: dateString,
      start: startOfDay,
      end: endOfDay,
      timestamp: now
    };

    return { start: startOfDay, end: endOfDay, dateString };
  },

  getTodayDate(): string {
    return this.getTodayRange().dateString;
  }
};

// ============================================================================
// SERVICE CLASS
// ============================================================================

class AttendanceService {
  private db: Firestore;
  private app: FirebaseApp;
  private listeners: Map<string, Unsubscribe> = new Map();
  private _checkInsCache: { data: CurrentCheckIn[]; timestamp: number } | null = null;
  private readonly CACHE_TTL = 30 * 1000; // 30 seconds for real-time data

  constructor() {
    this.app = initializeApp(firebaseConfig, 'attendanceFirestore');
    this.db = getFirestore(this.app);
  }

  /**
   * Convert timestamp to ISO string
   */
  private convertTimestampToString(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toISOString();
      }
      
      if (timestamp.seconds !== undefined) {
        const milliseconds = timestamp.seconds * 1000 + ((timestamp.nanoseconds || 0) / 1000000);
        return new Date(milliseconds).toISOString();
      }
      
      if (timestamp instanceof Date) {
        return timestamp.toISOString();
      }
      
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      if (typeof timestamp === 'number') {
        const date = timestamp < 10000000000 
          ? new Date(timestamp * 1000) 
          : new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      return '';
    } catch {
      return '';
    }
  }

  /**
   * Convert timestamp to Date object
   */
  private convertTimestampToDate(timestamp: any): Date | null {
    if (!timestamp) return null;
    
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      
      if (timestamp.seconds !== undefined) {
        const milliseconds = timestamp.seconds * 1000 + ((timestamp.nanoseconds || 0) / 1000000);
        return new Date(milliseconds);
      }
      
      if (timestamp instanceof Date) {
        return timestamp;
      }
      
      if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Extract date string from timestamp (YYYY-MM-DD in Dubai timezone)
   */
  private extractDateFromTimestamp(timestamp: any): string {
    const date = this.convertTimestampToDate(timestamp);
    if (!date) return '';
    
    const dubaiDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Dubai" }));
    const year = dubaiDate.getFullYear();
    const month = String(dubaiDate.getMonth() + 1).padStart(2, '0');
    const day = String(dubaiDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  /**
   * Normalize employee ID
   */
  private normalizeEmployeeId(employeeId: any): string {
    if (!employeeId) return '';
    return String(employeeId).trim();
  }

  /**
   * Convert Firestore data to AttendanceRecord with validation
   */
  private convertFirestoreToAttendanceRecord(
    docId: string,
    data: DocumentData
  ): AttendanceRecord | null {
    try {
      const today = DateCache.getTodayDate();
      
      // Validation: Check date field
      const dateFieldValid = data.date && data.date === today;
      
      // Validation: Check checkIn timestamp
      const checkInDate = this.extractDateFromTimestamp(data.checkIn);
      const checkInValid = checkInDate === today;
      
      // Validation: Check document ID
      const docIdDateMatch = docId.match(/_(\d{4}-\d{2}-\d{2})$/);
      const docIdValid = docIdDateMatch && docIdDateMatch[1] === today;
      
      // At least TWO validation methods must pass
      const validationsPassed = [dateFieldValid, checkInValid, docIdValid].filter(Boolean).length;
      
      if (validationsPassed < 2) {
        return null;
      }

      const record: AttendanceRecord = {
        employeeId: this.normalizeEmployeeId(data.employeeId || ''),
        checkIn: this.convertTimestampToString(data.checkIn),
        checkOut: data.checkOut ? this.convertTimestampToString(data.checkOut) : null,
        date: data.date || today,
        location: data.location || '',
        locationId: String(data.locationId || ''),
        locationLat: Number(data.locationLat) || 0,
        locationLng: Number(data.locationLng) || 0,
        isWithinGeofence: Boolean(data.isWithinGeofence),
        workStatus: data.workStatus || 'Unknown',
        totalHours: Number(data.totalHours) || 0
      };

      if (record.date !== today) {
        return null;
      }

      return record;
    } catch {
      return null;
    }
  }

  /**
   * Convert to current check-in with validation
   */
  private convertToCurrentCheckIn(record: AttendanceRecord): CurrentCheckIn | null {
    const today = DateCache.getTodayDate();
    
    // Must be from TODAY
    if (record.date !== today) {
      return null;
    }

    // Must NOT have checked out
    const hasCheckedOut = record.checkOut !== null && 
                         record.checkOut !== 'null' && 
                         record.checkOut !== '' && 
                         record.checkOut !== undefined;

    if (hasCheckedOut) {
      return null;
    }

    // Must have valid check-in
    if (!record.employeeId || !record.checkIn) {
      return null;
    }

    return {
      employeeId: record.employeeId,
      location: record.location,
      locationId: record.locationId,
      locationLat: record.locationLat,
      locationLng: record.locationLng,
      checkInTime: record.checkIn,
      workStatus: record.workStatus,
      isActive: true,
      isWithinGeofence: record.isWithinGeofence,
      totalHours: record.totalHours,
      checkInLocation: record.locationId,
      locationName: record.location
    };
  }

  /**
   * Get current check-ins with strict filtering
   */
  async getCurrentCheckIns(): Promise<CurrentCheckIn[]> {
    try {
      // Check cache
      if (this._checkInsCache && Date.now() - this._checkInsCache.timestamp < this.CACHE_TTL) {
        return this._checkInsCache.data;
      }

      const { dateString: today } = DateCache.getTodayRange();
      
      const attendanceRef = collection(this.db, 'Attendance_Records', 'PTSEmployees', 'Records');
      
      // Try to query by date field first
      let querySnapshot: QuerySnapshot<DocumentData>;
      try {
        const dateQuery = query(
          attendanceRef,
          where('date', '==', today),
          limit(1000)
        );
        querySnapshot = await getDocs(dateQuery);
      } catch {
        querySnapshot = await getDocs(attendanceRef);
      }
      
      const currentCheckIns: CurrentCheckIn[] = [];
      const processedEmployees = new Set<string>();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const docId = doc.id;
        
        // Validate date
        let isTodayRecord = false;
        
        if (data.date && data.date === today) {
          isTodayRecord = true;
        } else if (data.checkIn) {
          const checkInDate = this.extractDateFromTimestamp(data.checkIn);
          if (checkInDate === today) {
            isTodayRecord = true;
          }
        } else {
          const docIdDateMatch = docId.match(/_(\d{4}-\d{2}-\d{2})$/);
          if (docIdDateMatch && docIdDateMatch[1] === today) {
            isTodayRecord = true;
          }
        }
        
        if (!isTodayRecord) return;
        
        const attendanceRecord = this.convertFirestoreToAttendanceRecord(docId, data);
        if (!attendanceRecord) return;
        
        const currentCheckIn = this.convertToCurrentCheckIn(attendanceRecord);
        if (!currentCheckIn) return;
        
        // Prevent duplicates
        const employeeKey = `${currentCheckIn.employeeId}-${currentCheckIn.locationId}`;
        
        if (!processedEmployees.has(employeeKey)) {
          processedEmployees.add(employeeKey);
          currentCheckIns.push(currentCheckIn);
        }
      });

      // Cache the result
      this._checkInsCache = { data: currentCheckIns, timestamp: Date.now() };
      
      return currentCheckIns;
    } catch (error) {
      throw new Error('Failed to fetch current check-ins: ' + (error as Error).message);
    }
  }

  /**
   * Subscribe with strict filtering
   */
  subscribeToCurrentCheckIns(callback: CurrentCheckInsCallback): Unsubscribe {
    const attendanceRef = collection(this.db, 'Attendance_Records', 'PTSEmployees', 'Records');
    const listenerId = Math.random().toString(36).substr(2, 9);
    
    const unsubscribe: Unsubscribe = onSnapshot(
      attendanceRef,
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        try {
          const today = DateCache.getTodayDate();
          const currentCheckIns: CurrentCheckIn[] = [];
          const processedEmployees = new Set<string>();
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const docId = doc.id;
            
            const attendanceRecord = this.convertFirestoreToAttendanceRecord(docId, data);
            if (!attendanceRecord) return;
            
            const currentCheckIn = this.convertToCurrentCheckIn(attendanceRecord);
            if (!currentCheckIn) return;
            
            const employeeKey = `${currentCheckIn.employeeId}-${currentCheckIn.locationId}`;
            
            if (!processedEmployees.has(employeeKey)) {
              currentCheckIns.push(currentCheckIn);
              processedEmployees.add(employeeKey);
            }
          });

          // Update cache
          this._checkInsCache = { data: currentCheckIns, timestamp: Date.now() };
          
          callback(currentCheckIns);
        } catch {
          callback([]);
        }
      },
      () => {
        callback([]);
      }
    );

    this.listeners.set(listenerId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Get check-ins by location ID
   */
  async getCheckInsByLocationId(locationId: string): Promise<CurrentCheckIn[]> {
    const allCheckIns = await this.getCurrentCheckIns();
    return allCheckIns.filter(checkIn => 
      checkIn.locationId === locationId || 
      checkIn.locationId === String(locationId)
    );
  }

  /**
   * Get check-ins count by location
   */
  async getCheckInsCountByLocation(): Promise<Map<string, number>> {
    const checkIns = await this.getCurrentCheckIns();
    const countMap = new Map<string, number>();
    
    checkIns.forEach(checkIn => {
      const locationKey = checkIn.locationId;
      const current = countMap.get(locationKey) || 0;
      countMap.set(locationKey, current + 1);
    });
    
    return countMap;
  }

  /**
   * Get attendance statistics for today
   */
  async getTodayAttendanceStats(): Promise<{
    totalCheckedIn: number;
    byLocation: Record<string, number>;
    byLocationName: Record<string, number>;
    todayDate: string;
  }> {
    const checkIns = await this.getCurrentCheckIns();
    const today = DateCache.getTodayDate();
    
    const byLocation: Record<string, number> = {};
    const byLocationName: Record<string, number> = {};
    
    checkIns.forEach(checkIn => {
      byLocation[checkIn.locationId] = (byLocation[checkIn.locationId] || 0) + 1;
      byLocationName[checkIn.location] = (byLocationName[checkIn.location] || 0) + 1;
    });
    
    return {
      totalCheckedIn: checkIns.length,
      byLocation,
      byLocationName,
      todayDate: today
    };
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this._checkInsCache = null;
  }

  /**
   * Clean up all listeners
   */
  cleanup(): void {
    this.listeners.forEach((unsubscribe: Unsubscribe) => unsubscribe());
    this.listeners.clear();
    this._checkInsCache = null;
  }
}

export const attendanceService = new AttendanceService();
export default attendanceService;